import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import D3EDate from "../classes/D3EDate";
import AdminOrdersRequest from "../models/AdminOrdersRequest";
import MaterialIcons from "../icons/MaterialIcons";
import Result from "../classes/Result";
import Query from "../classes/Query";
import IconViewWithText from "./IconViewWithText";
import BooleanToggleView from "./BooleanToggleView";
import MessageDispatch from "../rocket/MessageDispatch";
import PortfolioProject from "../models/PortfolioProject";
import ListWrapper from "../utils/ListWrapper";
import BusinessTeleCallTypeCountView from "./BusinessTeleCallTypeCountView";
import Duration from "../core/Duration";
import TextView from "./TextView";
import BusinessInCallTypeCountView from "./BusinessInCallTypeCountView";
import CollectionUtils from "../utils/CollectionUtils";
import DropDown from "./DropDown";
import BusinessTotalOrdersCountView from "./BusinessTotalOrdersCountView";
import Provider from "../models/Provider";
import OrderStatus from "../classes/OrderStatus";
import ServiceCallType from "../classes/ServiceCallType";
import Pie2DChart from "./Pie2DChart";
import WidgetWrapper from "./WidgetWrapper";
import BusinessStatus from "../classes/BusinessStatus";
import BusinessOutCallTypeCountView from "./BusinessOutCallTypeCountView";
import CheckboxWithText from "./CheckboxWithText";
import AdminOrders from "../classes/AdminOrders";
import Bar2DChart from "./Bar2DChart";
import Order from "../models/Order";
import DateTime from "../core/DateTime";
import Business from "../models/Business";
import Divider from "./Divider";
import { UsageConstants } from "../rocket/D3ETemplate";
import { BuildContext } from "../classes/BuildContext";

export interface AdminBusinessDetailsPageProps extends BaseUIProps {
  key?: string;
  business: Business;
}

class _AdminBusinessDetailsPageState extends ObservableComponent<AdminBusinessDetailsPageProps> {
  static defaultProps = { business: null };
  dropDownRefFocusNode: ui.FocusNode = new ui.FocusNode();
  ordersList: Array<Order> = ListWrapper.widget(this, "ordersList");
  query: AdminOrders = null;
  isSuspended: boolean = false;
  isActiveBusiness: boolean = true;
  request: AdminOrdersRequest = null;
  fromDate: D3EDate = null;
  toDate: D3EDate = null;
  filterValue: string = "";
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AdminBusinessDetailsPageProps) {
    super(props);

    this.initState();
  }
  public get business(): Business {
    return this.props.business;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;

    this.onInit();
  }
  public initListeners(): void {
    this.updateSyncProperty("business", this.props.business);

    this.on(["query", "query.items"], this.computeOrdersList);

    this.computeOrdersList();

    this.on(
      [
        "request",
        "request.applyStatus",
        "request.business",
        "request.fromDate",
        "request.orderDate",
        "request.status",
        "request.toDate",
      ],
      this.computeQuery
    );

    this.computeQuery();

    this.on(
      [
        "business",
        "business.details",
        "business.logo",
        "business.name",
        "business.portfolioProjects",
        "business.portfolioProjects.attachments",
        "business.portfolioProjects.details",
        "business.portfolioProjects.title",
        "business.providers",
        "business.providers.business",
        "business.providers.business.address",
        "business.providers.business.address.fullAddress",
        "business.providers.business.name",
        "business.providers.email",
        "business.providers.iDProof",
        "business.providers.isManager",
        "business.providers.name",
        "business.providers.phone",
        "business.providers.profile",
        "filterValue",
        "isActiveBusiness",
        "isSuspended",
        "ordersList",
        "ordersList.callType",
      ],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: AdminBusinessDetailsPageProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.business !== this.props.business) {
      this.updateObservable(
        "business",
        prevProps.business,
        this.props.business
      );

      this.fire("business", this);
    }
  }
  public setOrdersList(val: Array<Order>): void {
    let isValChanged: boolean = CollectionUtils.isNotEquals(
      this.ordersList,
      val
    );

    if (!isValChanged) {
      return;
    }

    this.updateObservableColl("ordersList", this.ordersList, val);

    this.ordersList.clear();

    this.ordersList.addAll(val);

    this.fire("ordersList", this);
  }
  public addToOrdersList(val: Order, index: number = -1): void {
    if (index === -1) {
      if (!this.ordersList.contains(val)) this.ordersList.add(val);
    } else {
      this.ordersList.remove(this.ordersList.elementAt(index));

      this.ordersList.add(val);
    }

    this.fire("ordersList", this, val, true);

    this.updateObservable("ordersList", null, val);
  }
  public removeFromOrdersList(val: Order): void {
    this.ordersList.remove(val);

    this.fire("ordersList", this, val, false);

    this.removeObservable("ordersList", val);
  }
  public computeOrdersList = (): void => {
    try {
      this.setOrdersList(
        Array.from(this.query !== null ? this.query.items : [])
      );
    } catch (exception) {
      console.log(" exception in computeOrdersList : " + exception.toString());

      this.setOrdersList([]);
    }
  };
  public setQuery(val: AdminOrders): void {
    let isValChanged: boolean = this.query !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("query", this.query, val);

    MessageDispatch.get().dispose(this.query);

    this.query = val;

    this.fire("query", this);
  }
  public computeQuery = async (): Promise<void> => {
    try {
      this.setQuery(
        await Query.get().getAdminOrders(
          UsageConstants.QUERY_GETADMINORDERS_ADMINBUSINESSDETAILSPAGE_PROPERTIES_QUERY_COMPUTATION,
          this.request,
          { "synchronize": true }
        )
      );
    } catch (exception) {
      console.log(" exception in computeQuery : " + exception.toString());

      this.setQuery(null);
    }
  };
  public setIsSuspended(val: boolean): void {
    let isValChanged: boolean = this.isSuspended !== val;

    if (!isValChanged) {
      return;
    }

    this.isSuspended = val;

    this.fire("isSuspended", this);
  }
  public setIsActiveBusiness(val: boolean): void {
    let isValChanged: boolean = this.isActiveBusiness !== val;

    if (!isValChanged) {
      return;
    }

    this.isActiveBusiness = val;

    this.fire("isActiveBusiness", this);
  }
  public setRequest(val: AdminOrdersRequest): void {
    let isValChanged: boolean = this.request !== val;

    if (!isValChanged) {
      return;
    }

    this.updateObservable("request", this.request, val);

    this.request = val;

    this.fire("request", this);
  }
  public setFromDate(val: D3EDate): void {
    let isValChanged: boolean = this.fromDate !== val;

    if (!isValChanged) {
      return;
    }

    this.fromDate = val;

    this.fire("fromDate", this);
  }
  public setToDate(val: D3EDate): void {
    let isValChanged: boolean = this.toDate !== val;

    if (!isValChanged) {
      return;
    }

    this.toDate = val;

    this.fire("toDate", this);
  }
  public setFilterValue(val: string): void {
    let isValChanged: boolean = this.filterValue !== val;

    if (!isValChanged) {
      return;
    }

    this.filterValue = val;

    this.fire("filterValue", this);
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      children: [
        ui.Column({
          mainAxisAlignment: ui.MainAxisAlignment.start,
          crossAxisAlignment: ui.CrossAxisAlignment.start,
          children: [
            ui.Row({
              mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
              children: [
                TextView({
                  data: "Business Details View",
                  style: new ui.TextStyle({
                    fontSize: cStyle.tTextViewHeadlineTwoFontSizeOn,
                    fontWeight: cStyle.tTextViewHeadlineTwoFontWeightOn,
                  }),
                  className: "x452",
                  key: "0",
                }),
                ui.Container({
                  margin: ui.EdgeInsets.symmetric({
                    horizontal: 10.0,
                    vertical: 0.0,
                    transitions: new Map(),
                  }),
                  width: 200,
                  child: DropDown<string>({
                    items: ["Today", "Yesterday", "Current Week", "Last Month"],
                    value: this.filterValue,
                    onChanged: (value) => {
                      this.dropdownHandler(value);
                    },
                    onFocusChange: (val) => {},
                    builder: (context, item) => {
                      return TextView({ data: item });
                    },
                    focusNode: this.dropDownRefFocusNode,
                  }),
                  key: "1",
                  className: "x52 hc",
                }),
              ],
              className: "x3b hc h",
              key: "0",
            }),
            ui.Row({
              children: [
                ui.Container({
                  expand: true,
                  margin: ui.EdgeInsets.fromLTRB(
                    5.0,
                    5.0,
                    15.0,
                    5.0,
                    new Map()
                  ),
                  child: WidgetWrapper({
                    title: "Trends",
                    child: ui.Column({
                      children: [
                        ui.Row({
                          children: [
                            ui.Container({ className: "x238 hc vc", key: "0" }),
                            TextView({
                              data: "Categories",
                              className: "x8a",
                              key: "1",
                            }),
                          ],
                          className: "x31 hc h",
                          key: "0",
                        }),
                        ui.Container({
                          height: 350,
                          width: 300,
                          child: Bar2DChart({ data: [] }),
                          key: "1",
                          className: "x51 hc vc",
                        }),
                      ],
                      className: "x23 hc vc h v",
                    }),
                  }),
                  key: "0",
                  className: "xfff hc vc h",
                }),
                ui.Container({
                  expand: true,
                  margin: ui.EdgeInsets.all(5.0, new Map()),
                  child: WidgetWrapper({
                    title: "Performance Metrics",
                    child: ui.Row({
                      children: [
                        ui.Column({
                          children: [
                            ui.Container({
                              height: 350,
                              width: 300,
                              child: Pie2DChart({ data: [] }),
                              key: "0",
                              className: "x14 hc vc",
                            }),
                          ],
                          className: "xd8 vc v",
                          key: "0",
                        }),
                        ui.Column({
                          children: [
                            ui.Row({
                              children: [
                                ui.Container({
                                  className: "xf1 hc vc",
                                  key: "0",
                                }),
                                TextView({
                                  data: "Poor",
                                  className: "x1b",
                                  key: "1",
                                }),
                              ],
                              className: "xcb",
                              key: "0",
                            }),
                            ui.Row({
                              children: [
                                ui.Container({
                                  className: "x6b hc vc",
                                  key: "0",
                                }),
                                TextView({
                                  data: "Sufficient",
                                  className: "x2be",
                                  key: "1",
                                }),
                              ],
                              className: "x97",
                              key: "1",
                            }),
                            ui.Row({
                              children: [
                                ui.Container({
                                  className: "x3c hc vc",
                                  key: "0",
                                }),
                                TextView({
                                  data: "Good",
                                  className: "x6af",
                                  key: "1",
                                }),
                              ],
                              className: "x1f",
                              key: "2",
                            }),
                            ui.Row({
                              children: [
                                ui.Container({
                                  className: "x7dd hc vc",
                                  key: "0",
                                }),
                                TextView({
                                  data: "Very Good",
                                  className: "xc7",
                                  key: "1",
                                }),
                              ],
                              className: "xf9",
                              key: "3",
                            }),
                          ],
                          className: "x6a vc v",
                          key: "1",
                        }),
                      ],
                      className: "x458 hc vc h v",
                    }),
                  }),
                  key: "1",
                  className: "x2f hc vc h",
                }),
              ],
              className: "x772 hc vc h v",
              key: "1",
            }),
            ui.Row({
              children: [
                ui.Container({
                  expand: true,
                  margin: ui.EdgeInsets.fromLTRB(
                    5.0,
                    5.0,
                    15.0,
                    5.0,
                    new Map()
                  ),
                  child: WidgetWrapper({
                    title: "Revenue Reports",
                    child: ui.Column({
                      children: [
                        ui.Row({
                          mainAxisAlignment: ui.MainAxisAlignment.start,
                          children: [
                            ui.Row({
                              children: [
                                ui.Container({
                                  className: "x0a hc vc",
                                  key: "0",
                                }),
                                TextView({
                                  data: "Earning by provider",
                                  className: "x0f",
                                  key: "1",
                                }),
                              ],
                              key: "0",
                            }),
                            ui.Row({
                              children: [
                                ui.Container({
                                  className: "x920 hc vc",
                                  key: "0",
                                }),
                                TextView({
                                  data: "Commission by admin",
                                  className: "x58",
                                  key: "1",
                                }),
                              ],
                              className: "x8b",
                              key: "1",
                            }),
                          ],
                          className: "xf90 hc h",
                          key: "0",
                        }),
                      ],
                      className: "x63 hc vc h v",
                    }),
                  }),
                  key: "0",
                  className: "xd85 hc vc h",
                }),
                ui.Row({
                  children: [
                    ui.Column({
                      children: [
                        ui.Container({
                          expand: true,
                          margin: ui.EdgeInsets.fromLTRB(
                            5.0,
                            5.0,
                            7.5,
                            15.0,
                            new Map()
                          ),
                          child: BusinessInCallTypeCountView({
                            count: this.ordersList.where(
                              (o) => o.callType === ServiceCallType.InCall
                            ).length,
                          }),
                          key: "0",
                          className: "x40 hc vc h v",
                        }),
                        ui.Container({
                          expand: true,
                          margin: ui.EdgeInsets.fromLTRB(
                            7.5,
                            5.0,
                            5.0,
                            5.0,
                            new Map()
                          ),
                          child: BusinessOutCallTypeCountView({
                            count: this.ordersList.where(
                              (o) => o.callType === ServiceCallType.OutCall
                            ).length,
                          }),
                          key: "1",
                          className: "x637 hc vc h v",
                        }),
                      ],
                      className: "x7ca hc vc h v",
                      key: "0",
                    }),
                    ui.Column({
                      children: [
                        ui.Container({
                          expand: true,
                          margin: ui.EdgeInsets.fromLTRB(
                            5.0,
                            5.0,
                            7.5,
                            15.0,
                            new Map()
                          ),
                          child: BusinessTeleCallTypeCountView({
                            count: this.ordersList.where(
                              (o) => o.callType === ServiceCallType.TeleCall
                            ).length,
                          }),
                          key: "0",
                          className: "xdb hc vc h v",
                        }),
                        ui.Container({
                          expand: true,
                          margin: ui.EdgeInsets.fromLTRB(
                            7.5,
                            5.0,
                            5.0,
                            5.0,
                            new Map()
                          ),
                          child: BusinessTotalOrdersCountView({
                            count: this.ordersList.length,
                          }),
                          key: "1",
                          className: "xe0 hc vc h v",
                        }),
                      ],
                      className: "x8c hc vc h v",
                      key: "1",
                    }),
                  ],
                  className: "x1b1 hc vc h v",
                  key: "1",
                }),
              ],
              className: "xc8 hc vc h v",
              key: "2",
            }),
            ui.Column({
              children: [
                ui.Row({
                  crossAxisAlignment: ui.CrossAxisAlignment.start,
                  children: [
                    ui.Column({
                      crossAxisAlignment: ui.CrossAxisAlignment.start,
                      children: [
                        ui.Row({
                          children: [
                            TextView({
                              data: "Business Details",
                              style: new ui.TextStyle({
                                fontSize: cStyle.tTextViewInfoTextFontSizeOn,
                                fontWeight:
                                  cStyle.tTextViewInfoTextFontWeightOn,
                              }),
                              className: "x0e",
                              key: "0",
                            }),
                          ],
                          className: "x82d hc h",
                          key: "0",
                        }),
                        ui.Row({
                          children: [
                            ui.Row({
                              children: [
                                this.business.logo === null
                                  ? ui.AssetImage({
                                      path: "images/BusinessImage.png",
                                      width: 100,
                                      height: 100,
                                      fit: ui.BoxFit.cover,
                                      className: "x4c hc vc",
                                    })
                                  : ui.NetworkImage({
                                      url: this.business.logo.downloadUrl,
                                      width: 100,
                                      height: 100,
                                      fit: ui.BoxFit.cover,
                                      className: "x5b hc vc",
                                    }),
                                ui.Column({
                                  crossAxisAlignment:
                                    ui.CrossAxisAlignment.start,
                                  children: [
                                    TextView({
                                      data: "Business Name",
                                      style: new ui.TextStyle({
                                        color: cStyle.c4,
                                        fontSize:
                                          cStyle.tTextViewHeadlineSixFontSizeOn,
                                        fontWeight:
                                          cStyle.tTextViewHeadlineSixFontWeightOn,
                                      }),
                                      className: "x1ba",
                                      key: "0",
                                    }),
                                    TextView({
                                      data: this.business.name,
                                      style: new ui.TextStyle({
                                        fontSize:
                                          cStyle.tTextViewHeadlineFourFontSizeOn,
                                        fontWeight:
                                          cStyle.tTextViewHeadlineFourFontWeightOn,
                                      }),
                                      className: "x18",
                                      key: "1",
                                    }),
                                  ],
                                  key: "1",
                                }),
                              ],
                              className: "x06b hc h",
                              key: "0",
                            }),
                            ui.Column({
                              children: [
                                ui.Container({
                                  alignment: ui.Alignment.center,
                                  className: "x0bc hc",
                                  key: "0",
                                }),
                              ],
                              key: "1",
                            }),
                            ui.Column({
                              children: [
                                ui.Row({
                                  children: [
                                    TextView({
                                      data: "Status",
                                      style: new ui.TextStyle({
                                        color: cStyle.tTextViewLabelTextColorOn,
                                        fontSize:
                                          cStyle.tTextViewLabelTextFontSizeOn,
                                        fontWeight:
                                          cStyle.tTextViewLabelTextFontWeightOn,
                                      }),
                                      className: "x3ba",
                                      key: "0",
                                    }),
                                    BooleanToggleView({
                                      activeColor: cStyle.c15,
                                      inActiveColor: cStyle.c1,
                                      activeTextColor: cStyle.c5,
                                      inActiveTextColor: cStyle.c5,
                                      value: this.isActiveBusiness,
                                      onChanged: (value) => {
                                        this.toggleButtonHandler(value);
                                      },
                                      key: "1",
                                    }),
                                  ],
                                  className: "x635 hc h",
                                  key: "0",
                                }),
                                CheckboxWithText({
                                  title: "Suspend",
                                  value: this.isSuspended,
                                  onChanged: (value) => {
                                    this.onSuspensionToggleHandler(value);
                                  },
                                  className: "xb2 hc h",
                                  key: "1",
                                }),
                              ],
                              className: "x8c8 hc h",
                              key: "2",
                            }),
                          ],
                          className: "x6f hc h",
                          key: "1",
                        }),
                        TextView({
                          data: "Description",
                          style: new ui.TextStyle({
                            color: cStyle.c4,
                            fontSize: cStyle.tTextViewLabelTextFontSizeOn,
                            fontWeight: cStyle.tTextViewLabelTextFontWeightOn,
                          }),
                          className: "x38 hc",
                          key: "2",
                        }),
                        TextView({
                          data: this.business.details,
                          softWrap: true,
                          maxLines: 3,
                          className: "x74 hc",
                          key: "3",
                        }),
                        ui.Column({
                          crossAxisAlignment: ui.CrossAxisAlignment.start,
                          children: [
                            TextView({
                              data: "Portfolio Projects",
                              style: new ui.TextStyle({
                                fontSize:
                                  cStyle.tTextViewHeadlineFourFontSizeOn,
                                fontWeight:
                                  cStyle.tTextViewHeadlineFourFontWeightOn,
                              }),
                              className: "x1c hc",
                              key: "0",
                            }),
                            Divider({ className: "xf92 hc h", key: "1" }),
                            this.business.portfolioProjects.isNotEmpty
                              ? this.business.portfolioProjects.expand(
                                  (project) => [
                                    ui.Row({
                                      crossAxisAlignment:
                                        ui.CrossAxisAlignment.start,
                                      children: [
                                        project.attachments.isEmpty
                                          ? ui.AssetImage({
                                              path: "images/Kitchen.jpg",
                                              height: 100,
                                              width: 100,
                                              fit: ui.BoxFit.cover,
                                              className: "x8a0 hc vc",
                                            })
                                          : ui.NetworkImage({
                                              url:
                                                project?.attachments?.first
                                                  ?.downloadUrl ?? "",
                                              height: 100,
                                              width: 100,
                                              fit: ui.BoxFit.cover,
                                              className: "xba6 hc vc",
                                            }),
                                        ui.Column({
                                          crossAxisAlignment:
                                            ui.CrossAxisAlignment.start,
                                          children: [
                                            TextView({
                                              data: project.title,
                                              style: new ui.TextStyle({
                                                fontSize:
                                                  cStyle.tTextViewHeadlineFiveFontSizeOn,
                                                fontWeight:
                                                  cStyle.tTextViewHeadlineFiveFontWeightOn,
                                              }),
                                              className: "x2c6 hc",
                                              key: "0",
                                            }),
                                            TextView({
                                              data: project.details,
                                              softWrap: true,
                                              maxLines: 3,
                                              className: "x0b0 hc",
                                              key: "1",
                                            }),
                                          ],
                                          className: "x4d hc h",
                                          key: "1",
                                        }),
                                      ],
                                      className: "xb7 hc h",
                                      key: project?.ident,
                                    }),
                                  ]
                                )
                              : TextView({
                                  data: "No Projects Available",
                                  style: new ui.TextStyle({
                                    fontWeight: ui.FontWeight.bold,
                                  }),
                                  className: "x3a4 hc",
                                }),
                          ],
                          className: "x066 hc h",
                          key: "4",
                        }),
                      ],
                      className: "x88 hc h",
                      key: "0",
                    }),
                    ui.Column({
                      crossAxisAlignment: ui.CrossAxisAlignment.start,
                      children: [
                        ui.Row({
                          children: [
                            TextView({
                              data: "Owner Details",
                              style: new ui.TextStyle({
                                fontSize: cStyle.tTextViewInfoTextFontSizeOn,
                                fontWeight:
                                  cStyle.tTextViewInfoTextFontWeightOn,
                              }),
                              className: "x86",
                              key: "0",
                            }),
                          ],
                          className: "x35 hc h",
                          key: "0",
                        }),
                        this.business.providers
                          .where((p) => p.isManager)
                          .expand((provider) => [
                            ui.Column({
                              crossAxisAlignment: ui.CrossAxisAlignment.start,
                              children: [
                                ui.Row({
                                  crossAxisAlignment:
                                    ui.CrossAxisAlignment.start,
                                  children: [
                                    provider.profile === null
                                      ? ui.ClipRRect({
                                          borderRadius:
                                            ui.BorderRadius.circular(7),
                                          child: ui.AssetImage({
                                            path: "images/Addperson.png",
                                            width: 100,
                                            height: 100,
                                            fit: ui.BoxFit.cover,
                                            className: "x28 hc vc",
                                          }),
                                        })
                                      : ui.ClipRRect({
                                          borderRadius:
                                            ui.BorderRadius.circular(7),
                                          child: ui.NetworkImage({
                                            url:
                                              provider?.profile?.downloadUrl ??
                                              "",
                                            width: 100,
                                            height: 100,
                                            fit: ui.BoxFit.cover,
                                            className: "x86b hc vc",
                                          }),
                                        }),
                                    ui.Column({
                                      crossAxisAlignment:
                                        ui.CrossAxisAlignment.start,
                                      children: [
                                        TextView({
                                          data: "Owner Name",
                                          style: new ui.TextStyle({
                                            color: cStyle.c4,
                                            fontSize:
                                              cStyle.tTextViewHeadlineSixFontSizeOn,
                                            fontWeight:
                                              cStyle.tTextViewHeadlineSixFontWeightOn,
                                          }),
                                          className: "xe2",
                                          key: "0",
                                        }),
                                        TextView({
                                          data: provider.name,
                                          style: new ui.TextStyle({
                                            fontSize:
                                              cStyle.tTextViewHeadlineFourFontSizeOn,
                                            fontWeight:
                                              cStyle.tTextViewHeadlineFourFontWeightOn,
                                          }),
                                          className: "xe20",
                                          key: "1",
                                        }),
                                        TextView({
                                          data: "Business Name",
                                          style: new ui.TextStyle({
                                            color: cStyle.c4,
                                            fontSize:
                                              cStyle.tTextViewHeadlineSixFontSizeOn,
                                            fontWeight:
                                              cStyle.tTextViewHeadlineSixFontWeightOn,
                                          }),
                                          className: "x20",
                                          key: "2",
                                        }),
                                        TextView({
                                          data: provider.business.name,
                                          style: new ui.TextStyle({
                                            fontSize:
                                              cStyle.tTextViewHeadlineFourFontSizeOn,
                                            fontWeight:
                                              cStyle.tTextViewHeadlineFourFontWeightOn,
                                          }),
                                          className: "xdd",
                                          key: "3",
                                        }),
                                      ],
                                      key: "1",
                                    }),
                                  ],
                                  className: "x10 hc h",
                                  key: "0",
                                }),
                                ui.Column({
                                  crossAxisAlignment:
                                    ui.CrossAxisAlignment.start,
                                  children: [
                                    ui.Container({
                                      child: IconViewWithText({
                                        icon: MaterialIcons.art_track,
                                        value: "Address",
                                        size: 30,
                                        onTap: (e) => {
                                          e.stopPropagation();

                                          this.onTapContactBehaviourHandler(
                                            provider
                                          );
                                        },
                                      }),
                                      key: "0",
                                      className: "x302 hc h",
                                    }),
                                    TextView({
                                      data:
                                        provider?.business?.address
                                          ?.fullAddress ?? "",
                                      softWrap: true,
                                      style: new ui.TextStyle({
                                        fontSize:
                                          cStyle.tTextViewHeadlineFiveFontSizeOn,
                                        fontWeight:
                                          cStyle.tTextViewHeadlineFiveFontWeightOn,
                                      }),
                                      className: "x57 hc",
                                      key: "1",
                                    }),
                                    ui.Container({
                                      child: IconViewWithText({
                                        icon: MaterialIcons.phone,
                                        value: "Mobile Numbers",
                                        size: 25,
                                        onTap: (e) => {
                                          e.stopPropagation();

                                          this.onTapAddressBehaviourHandler(
                                            provider
                                          );
                                        },
                                      }),
                                      key: "2",
                                      className: "xb98 hc h",
                                    }),
                                    TextView({
                                      data: provider.phone,
                                      style: new ui.TextStyle({
                                        fontSize:
                                          cStyle.tTextViewHeadlineFiveFontSizeOn,
                                        fontWeight:
                                          cStyle.tTextViewHeadlineFiveFontWeightOn,
                                      }),
                                      className: "xf16 hc",
                                      key: "3",
                                    }),
                                    ui.Container({
                                      child: IconViewWithText({
                                        icon: MaterialIcons.email,
                                        value: "Email Address",
                                        size: 25,
                                      }),
                                      key: "4",
                                      className: "xb0 hc h",
                                    }),
                                    TextView({
                                      data: provider.email,
                                      style: new ui.TextStyle({
                                        fontSize:
                                          cStyle.tTextViewHeadlineFiveFontSizeOn,
                                        fontWeight:
                                          cStyle.tTextViewHeadlineFiveFontWeightOn,
                                      }),
                                      className: "x35e hc",
                                      key: "5",
                                    }),
                                  ],
                                  className: "xe1 hc h",
                                  key: "1",
                                }),
                                ui.Column({
                                  crossAxisAlignment:
                                    ui.CrossAxisAlignment.start,
                                  children: [
                                    TextView({
                                      data: "ID Proof",
                                      style: new ui.TextStyle({
                                        fontSize:
                                          cStyle.tTextViewHeadlineFourFontSizeOn,
                                        fontWeight:
                                          cStyle.tTextViewHeadlineFourFontWeightOn,
                                      }),
                                      className: "xe1b hc",
                                      key: "0",
                                    }),
                                    ui.Column({
                                      crossAxisAlignment:
                                        ui.CrossAxisAlignment.start,
                                      children: [
                                        ui.NetworkImage({
                                          url:
                                            provider?.iDProof?.downloadUrl ??
                                            "",
                                          height: 150,
                                          width: 300,
                                          fit: ui.BoxFit.cover,
                                          className: "x9f hc vc",
                                          key: "0",
                                        }),
                                        TextView({
                                          data: provider.iDProof.name,
                                          style: new ui.TextStyle({
                                            color:
                                              cStyle.tTextViewLabelTextColorOn,
                                            fontSize:
                                              cStyle.tTextViewLabelTextFontSizeOn,
                                            fontWeight:
                                              cStyle.tTextViewLabelTextFontWeightOn,
                                          }),
                                          className: "xd0 hc",
                                          key: "1",
                                        }),
                                      ],
                                      className: "x966 hc",
                                      key: "1",
                                    }),
                                  ],
                                  className: "xee hc",
                                  key: "2",
                                }),
                              ],
                              className: "xfc hc h",
                              key: provider?.ident,
                            }),
                          ]),
                      ],
                      className: "x25 hc h",
                      key: "1",
                    }),
                  ],
                  className: "x2e hc h",
                  key: "0",
                }),
              ],
              className: "xd7 hc vc h v",
              key: "3",
            }),
          ],
          className: "x64 hc vc h v",
          key: "0",
        }),
      ],
      className: ui.join(
        this.props.className,
        "AdminBusinessDetailsPage hc vc"
      ),
      ...copyBaseUIProps(this.props),
    });
  }
  public onInit = (): void => {
    this.setRequest(
      new AdminOrdersRequest({
        business: this.business,
        applyStatus: true,
        status: OrderStatus.Completed,
        fromDate: D3EDate.now(),
        toDate: D3EDate.now(),
      })
    );
  };
  public onSetDatesInRequest = (): void => {
    this.request.setFromDate(this.fromDate);

    this.request.setToDate(this.toDate);
  };
  public dropdownHandler = (value: string): void => {
    this.setFilterValue(value);

    if (this.filterValue === "Today") {
      this.setFilterValue("Today");

      this.request.setFromDate(D3EDate.now());

      this.setToDate(D3EDate.now());

      this.onSetDatesInRequest();
    } else if (this.filterValue === "Yesterday") {
      this.setFilterValue("Yesterday");

      let today: D3EDate = D3EDate.now();

      this.setFromDate(
        D3EDate.of(today.year, today.month, today.dayOfMonth - 1)
      );

      this.setToDate(D3EDate.of(today.year, today.month, today.dayOfMonth - 1));

      this.onSetDatesInRequest();
    } else if (this.filterValue === "Current Week") {
      this.setFilterValue("Current Week");

      let dateTime: DateTime = DateTime.now();

      let firstDayOfWeek: DateTime = dateTime.subtract(
        new Duration({ days: dateTime.weekday - 1 })
      );

      this.setFromDate(
        D3EDate.of(
          firstDayOfWeek.year,
          firstDayOfWeek.month,
          firstDayOfWeek.day
        )
      );

      this.setToDate(D3EDate.now());

      this.onSetDatesInRequest();
    } else if (this.filterValue === "Last Month") {
      this.setFilterValue("Last Month");

      let today: D3EDate = D3EDate.now();

      this.setFromDate(D3EDate.of(today.year, today.month - 1, 1));

      this.setToDate(D3EDate.of(today.year, today.month - 1, 1));

      this.onSetDatesInRequest();
    }
  };
  public toggleButtonHandler = async (value: boolean): Promise<void> => {
    this.setIsActiveBusiness(value);

    if (this.isActiveBusiness) {
      this.business.setBusinessStatus(BusinessStatus.Active);
    } else {
      this.business.setBusinessStatus(BusinessStatus.Inactive);
    }

    let result: Result<Business> = await this.business.save();
  };
  public onSuspensionToggleHandler = async (value: boolean): Promise<void> => {
    this.setIsSuspended(value);

    if (this.isSuspended) {
      this.business.setBusinessStatus(BusinessStatus.Suspended);

      let result: Result<Business> = await this.business.save();
    }
  };
  public onTapContactBehaviourHandler = (provider: Provider): void => {
    //   UrlLauncher.openGoogleMaps(latitude : provider.business.address.location.latitude, longitude : provider.business.address.location.longitude);
  };
  public onTapAddressBehaviourHandler = (provider: Provider): void => {
    //  UrlLauncher.openDialPad(phoneNumber: provider.phone);
  };
  public dispose(): void {
    MessageDispatch.get().dispose(this.query);

    super.dispose();
  }
}
export default function AdminBusinessDetailsPage(
  props: AdminBusinessDetailsPageProps
) {
  return React.createElement(_AdminBusinessDetailsPageState, {
    ..._AdminBusinessDetailsPageState.defaultProps,
    ...props,
  });
}
